export const CONTENT_STATS_FUNCTION = 'getContentStats'

// Months are UTC, like Amplitude. The server refuses months before this one.
export const FIRST_MONTH = '2026-08'
// Favorites reach Amplitude from October 30, 2026, so favorite lists start with the first full month.
export const FAVORITES_START_MONTH = '2026-11'
// For the first days of a month there's too little data, so the page opens on the previous month.
export const NEW_MONTH_DAYS = 7
// A workout needs this many members before its rates count: at least MIN_MEMBERS, or 5% of the
// coach's top workout, whichever is larger. 15 members is about Leo's minimum for a small coach.
export const MIN_MEMBERS = 15
export const MIN_SHARE_OF_TOP = 0.05
export const LIST_LENGTH = 10

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const pad = (n) => String(n).padStart(2, '0')
const toMonth = (year, monthIndex) => {
  const d = new Date(Date.UTC(year, monthIndex, 1))
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`
}

export function getMonthLabel(month) {
  const [year, monthNumber] = month.split('-').map(Number)
  return `${MONTH_NAMES[monthNumber - 1]} ${year}`
}

// "September" for "2026-10"
export function getPreviousMonthName(month) {
  const monthNumber = Number(month.split('-')[1])
  return MONTH_NAMES[(monthNumber + 10) % 12]
}

// A running month is compared with the same days of last month, a past one with all of it
export const isCurrentMonth = (month, now = new Date()) => month === toMonth(now.getUTCFullYear(), now.getUTCMonth())

// Newest first: the current month so far, then every past month back to FIRST_MONTH.
export function getMonthOptions(now = new Date()) {
  const options = []
  for (let i = 0; ; i++) {
    const month = toMonth(now.getUTCFullYear(), now.getUTCMonth() - i)
    if (month < FIRST_MONTH) break
    options.push({ value: month, label: i === 0 ? `${getMonthLabel(month)} so far` : getMonthLabel(month) })
  }
  return options
}

// A new month's numbers are too thin and noisy for the first couple of days, so a month shows from
// this day on
export const MONTH_READY_DAY = 3

export function isMonthReady(month, now = new Date()) {
  const current = toMonth(now.getUTCFullYear(), now.getUTCMonth())
  return month < current || now.getUTCDate() >= MONTH_READY_DAY
}

export function getDefaultMonth(now = new Date()) {
  const current = toMonth(now.getUTCFullYear(), now.getUTCMonth())
  const previous = toMonth(now.getUTCFullYear(), now.getUTCMonth() - 1)
  return now.getUTCDate() <= NEW_MONTH_DAYS && previous >= FIRST_MONTH ? previous : current
}

/*
Opens and finishes count every time, except that the same member and item on the same day counts
once (openDays and completionDays). That also drops an app logging one finish many times. Members
(openers, completers) count each member once for the month.
*/
// Finishes after each member's first: a member who finishes on 10 different days adds 9.
export const getRepeatDays = (row) => Math.max(0, (row.completionDays || 0) - (row.completers || 0))

const ratio = (part, whole) => (whole > 0 ? part / whole : 0)
// Finishes out of opens. Both use the same counting rule, so they can be divided.
export const getFinishRate = (row) => ratio(row.completionDays || 0, row.openDays || 0)
const hasFinishRate = (row) => row.openDays > 0 && row.completionDays <= row.openDays
// Repeats per member who finished it
export const getRepeatRate = (row) => ratio(getRepeatDays(row), row.completers || 0)

const getMinimum = (rows, key) =>
  Math.max(MIN_MEMBERS, Math.round(Math.max(0, ...rows.map((row) => row[key] || 0)) * MIN_SHARE_OF_TOP))

// Items without a name are never shown: a nameless row would only raise questions coaches can't answer
const hasName = (name) => typeof name === 'string' && name.trim() !== ''
const ofType = (rows, type) => (rows || []).filter((row) => row && row.type === type && hasName(row.title))

// Totals for one type, summed over items
export function getTotals(rows, type) {
  return ofType(rows, type).reduce(
    (totals, row) => ({
      opens: totals.opens + (row.openDays || 0),
      finishes: totals.finishes + (row.completionDays || 0),
      repeatDays: totals.repeatDays + getRepeatDays(row),
    }),
    { opens: 0, finishes: 0, repeatDays: 0 }
  )
}

// The page's top cards: workouts and videos together. A session is one workout or video.
export function getSessionTotals(stats) {
  const sessions = (stats.rows || []).reduce((sum, row) => sum + (row.completionDays || 0), 0)
  const members = stats.sessionMembers || 0
  return { members, sessions, perMember: members > 0 ? sessions / members : null }
}

// Percent change, or null when there is nothing to compare with
export const getChange = (current, previous) => (previous > 0 ? ((current - previous) / previous) * 100 : null)

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0
}

const thirds = (values) => {
  const sorted = [...values].sort((a, b) => a - b)
  return [sorted[Math.floor(sorted.length / 3)], sorted[Math.floor((sorted.length * 2) / 3)]]
}

// Where a workout's finish and repeat rates sit among the coach's other workouts: the top third is
// above typical, the bottom third below. Only workouts with enough members count, and with fewer
// than 6 of them there's no meaningful "typical", so no colors.
export function getScoreBands(rows, type) {
  const items = ofType(rows, type)
  const minOpens = getMinimum(items, 'openDays')
  const minCompleters = getMinimum(items, 'completers')
  const finish = items.filter((row) => row.openDays >= minOpens && hasFinishRate(row)).map(getFinishRate)
  const repeat = items.filter((row) => row.completers >= minCompleters).map(getRepeatRate)
  return {
    finish: finish.length >= 6 ? thirds(finish) : null,
    repeat: repeat.length >= 6 ? thirds(repeat) : null,
    typicalFinish: finish.length >= 6 ? median(finish) : null,
  }
}

export const getBand = (value, band) =>
  !band ? 'neutral' : value >= band[1] ? 'good' : value < band[0] ? 'bad' : 'neutral'

// The two scores under a leaderboard row
export function getRowScores(row, bands) {
  const scores = []
  if (hasFinishRate(row)) {
    const rate = getFinishRate(row)
    scores.push({ key: 'finish', label: `${Math.round(rate * 100)}% finished`, band: getBand(rate, bands.finish) })
  }
  if (row.completers > 0) {
    const rate = getRepeatRate(row)
    scores.push({ key: 'repeat', label: `${rate.toFixed(1)} repeats each`, band: getBand(rate, bands.repeat) })
  }
  return scores
}

const rankBy = (items, key) => {
  const ranks = new Map()
  ;[...items]
    .sort((a, b) => (b[key] || 0) - (a[key] || 0) || (b.openDays || 0) - (a.openDays || 0))
    .forEach((row, index) => ranks.set(row, index + 1))
  return ranks
}

/*
Three takeaways, each a fixed rule over the month's numbers. A takeaway shows only when its rule
finds a workout; none of them is written by AI.
  All-rounder: top 5 in opens, finishes and repeats, so its lead isn't just placement.
  Hidden gem: outside the top 10 by opens (so it isn't in the Most opened list), the most repeats
    per member who finished it, among workouts with enough members who finished it.
  Rarely finished: among the top 5 by opens, the lowest finish rate, if it's under half the typical.
*/
export function getTakeaways(rows, type) {
  const items = ofType(rows, type).map((row) => ({ ...row, repeatDays: getRepeatDays(row) }))
  if (!items.length) return []
  const byOpens = rankBy(items, 'openDays')
  const byCompletions = rankBy(items, 'completionDays')
  const byRepeats = rankBy(items, 'repeatDays')
  const takeaways = []

  const allRounders = items.filter(
    (row) => byOpens.get(row) <= 5 && byCompletions.get(row) <= 5 && byRepeats.get(row) <= 5 && row.repeatDays > 0
  )
  if (allRounders.length) {
    const row = allRounders.sort(
      (a, b) =>
        byOpens.get(a) +
        byCompletions.get(a) +
        byRepeats.get(a) -
        (byOpens.get(b) + byCompletions.get(b) + byRepeats.get(b))
    )[0]
    takeaways.push({
      key: 'allRounder',
      row,
      ranks: { opens: byOpens.get(row), finishes: byCompletions.get(row), repeats: byRepeats.get(row) },
    })
  }

  const minCompleters = getMinimum(items, 'completers')
  const gems = items
    .filter((row) => byOpens.get(row) > LIST_LENGTH && row.completers >= minCompleters && row.repeatDays > 0)
    .sort((a, b) => getRepeatRate(b) - getRepeatRate(a))
  if (gems.length) {
    const row = gems[0]
    takeaways.push({ key: 'hiddenGem', row, ranks: { opens: byOpens.get(row) } })
  }

  const { typicalFinish } = getScoreBands(rows, type)
  const minOpens = getMinimum(items, 'openDays')
  const unfinished = items
    .filter((row) => byOpens.get(row) <= 5 && row.openDays >= minOpens && hasFinishRate(row))
    .sort((a, b) => getFinishRate(a) - getFinishRate(b))[0]
  if (unfinished && typicalFinish && getFinishRate(unfinished) < typicalFinish / 2) {
    takeaways.push({ key: 'rarelyFinished', row: unfinished, typicalFinish })
  }
  return takeaways
}

const contentSubtitle = (row) => (row.programId && row.programName) || null

export const LEADERBOARDS = [
  {
    key: 'popular',
    title: 'Most opened',
    caption: 'opens',
    icon: 'opens',
    description: 'Times opened (once per member per day)',
    value: (row) => row.openDays || 0,
  },
  {
    key: 'completed',
    title: 'Most finished',
    caption: 'finishes',
    icon: 'finishes',
    description: 'Finish % = finishes ÷ opens',
    value: (row) => row.completionDays || 0,
  },
  {
    key: 'repeated',
    title: 'Most repeated',
    caption: 'repeats',
    icon: 'repeats',
    description: 'Repeats each = repeats per member who finished it',
    value: getRepeatDays,
  },
]

export const FAVORITE_LISTS = [
  {
    key: 'favorited',
    title: 'Most favorited',
    caption: 'members',
    icon: 'favorites',
    value: (row) => row.favoriters || 0,
  },
  {
    key: 'favoriteRate',
    title: 'Favorite rate',
    caption: 'of members who opened it',
    icon: 'favorites',
    value: (row) => ((row.openers || 0) >= MIN_MEMBERS ? (row.favoriters || 0) / row.openers : 0),
    format: (value) => `${Math.round(value * 100)}%`,
  },
]

// A program needs this many members who finished a workout in it before its average shows.
export const MIN_MEMBERS_FOR_AVERAGE = 20

export const PROGRAM_LISTS = [
  {
    key: 'programMembers',
    title: 'Most members',
    caption: 'members',
    icon: 'members',
    value: (program) => program.completers || 0,
  },
  {
    key: 'programDays',
    title: 'Most days per member',
    caption: 'days / member',
    icon: 'perMember',
    value: (program) =>
      (program.completers || 0) >= MIN_MEMBERS_FOR_AVERAGE ? (program.completionDays || 0) / program.completers : 0,
    format: (value) => value.toFixed(1),
  },
]

const defaultFormat = (value) => value.toLocaleString()

const rank = (lists, items, tieBreak, length = LIST_LENGTH) =>
  lists.map((list) => ({
    ...list,
    format: list.format || defaultFormat,
    items: items
      .map((row) => ({ row, value: list.value(row) }))
      .filter(({ value }) => value > 0)
      .sort((a, b) => b.value - a.value || tieBreak(b.row) - tieBreak(a.row))
      .slice(0, length),
  }))

const withDisplay = (rows, type) =>
  ofType(rows, type).map((row) => ({
    ...row,
    key: `${row.programId || ''}-${row.title}`,
    subtitle: contentSubtitle(row),
  }))

// The three leaderboards for workouts or videos, top `length` each
export const getLeaderboards = (rows, type, length = LIST_LENGTH) =>
  rank(LEADERBOARDS, withDisplay(rows, type), (row) => row.openDays || 0, length)

export const getFavoriteLists = (rows, type) => rank(FAVORITE_LISTS, withDisplay(rows, type), (row) => row.openers || 0)

export function getProgramLists(programs = []) {
  const items = programs
    .filter((program) => hasName(program.programName))
    .map((program) => ({ ...program, key: program.programId, title: program.programName, subtitle: null }))
  return rank(PROGRAM_LISTS, items, (program) => program.completers || 0)
}

export const hasFavoriteData = (month) => month >= FAVORITES_START_MONTH

// Videos opened on their own, outside a program, only record a finish from November 2026, when
// the app began sending VideoWatchedToEnd. Videos inside programs recorded finishes all along. So
// before that month the Videos tab shows program videos only, with every stat.
export const ALL_VIDEOS_START_MONTH = '2026-11'
export const PROGRAM_VIDEOS_NOTE =
  'Before November 2026, only videos in your programs are counted. Videos added directly to Explore were not, but are counted from November 2026 onward.'
// Shown on the Videos tab for months before November 2026, when Explore videos join
export const VIDEO_FUNNEL_NOTE =
  'Before November 2026, only videos inside programs were counted. From November 2026 onward, videos added directly into Explore and not in a program are counted too.'
export const showsVideoFunnelNote = (type, month) => type === 'video' && month < ALL_VIDEOS_START_MONTH

export const isProgramVideosOnly = (type, month) => type === 'video' && month < ALL_VIDEOS_START_MONTH

// The rows a tab shows for a month
export const getVisibleRows = (rows, type, month) =>
  isProgramVideosOnly(type, month)
    ? (rows || []).filter((row) => row && row.type === type && row.programId)
    : (rows || []).filter((row) => row && hasName(row.title))

// Video totals change what they include in November 2026, so the change from the month before is
// left out until both months count the same videos
export const showsChange = (type, month) => type !== 'video' || month > ALL_VIDEOS_START_MONTH

// A tab shows only when the month has enough activity to say something: at least this many
// workouts, videos or programs that members used
export const MIN_ITEMS_FOR_TAB = 5

// The tabs worth showing for a month, in tab order
export function getAvailableTypes(stats, month) {
  const opened = (type) => getVisibleRows(stats.rows, type, month).filter((row) => row.type === type && row.openDays > 0)
  const programs = getProgramLists(stats.programs || [])[0].items
  const counts = { workout: opened('workout').length, video: opened('video').length, program: programs.length }
  return ['workout', 'video', 'program'].filter((type) => counts[type] >= MIN_ITEMS_FOR_TAB)
}

// Must match STATS_VERSION in firebase-cloud functions/v2/getContentStatsLogic.js. A saved month with
// another version is fetched again by the cloud function.
export const STATS_VERSION = 4
const DAY_MS = 24 * 60 * 60 * 1000

// What a saved month means for loading: 'fresh' shows it straight away, 'stale' is refreshed by the
// cloud function, 'missing' is fetched from Amplitude for the first time. Same rule as the function.
export function getSavedState(saved, now = Date.now()) {
  if (!saved || saved.version !== STATS_VERSION || !Number.isFinite(saved.updatedAt)) return 'missing'
  return saved.isFinal || now - saved.updatedAt < DAY_MS ? 'fresh' : 'stale'
}
